/**
 * Post Show State - Data State Management
 * Manages post data, editing state, and related information
 * Extracted from post-show-model.js and controller state management
 */

import { phpAuthUser } from "../../core/php-data-access";
import { Patterns } from "../../constants/patterns";
import type { RawPostData } from "../post-types";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { postShowForm } from "./post-show-form";

export interface PostShowStatePostData extends RawPostData {
    author?: string;
    created_at?: string;
    likes_count?: number;
    is_liked?: boolean;
    [key: string]: unknown;
}

export interface EditingPostState {
    id: number;
    originalData: PostShowStatePostData | null;
    isEditing: boolean;
}

export interface PostShowStateError {
    message: string;
    timestamp: string;
}

class PostShowStateHandler {
    public currentPost: PostShowStatePostData | null;
    public editingPost: EditingPostState | null;
    public relatedPosts: PostShowStatePostData[];
    public boardId: string | null;
    public userId: number | null;
    public isAutoRead: boolean;
    public isLoading: boolean;
    public errors: PostShowStateError[];

    public constructor() {
        this.currentPost = null;
        this.editingPost = null;
        this.relatedPosts = [];
        this.boardId = null;
        this.userId = null;
        this.isAutoRead = false;
        this.isLoading = false;
        this.errors = [];
    }

    public init(boardId: string | null = null, userId: number | null = null): void {
        this.boardId = boardId;
        this.userId = userId;
        this.loadCurrentPageData();
    }

    public loadCurrentPageData(): void {
        if (!this.boardId) {
            this.boardId = this.getBoardIdFromPage();
        }

        const postData = this.getCurrentPostFromPage();
        if (postData) {
            this.currentPost = postData;
        }

        if (!this.userId) {
            this.userId = this.getUserIdFromPage();
        }
    }

    public getBoardIdFromPage(): string | null {
        const postDetail = postShowForm.getPostDetail(document);
        if (postDetail instanceof HTMLElement && postDetail.dataset.boardId) {
            return postDetail.dataset.boardId;
        }

        const metaTag = postShowForm.getBoardIdMeta(document);
        if (metaTag instanceof HTMLMetaElement && metaTag.content) {
            return metaTag.content;
        }

        const bodyBoardId = document.body.dataset.boardId;
        if (bodyBoardId) {
            return bodyBoardId;
        }

        const pathMatch = window.location.pathname.match(Patterns.PATHNAME_POSTS_ID_WORD);
        if (pathMatch) {
            return pathMatch[1] ?? null;
        }

        return null;
    }

    public getCurrentPostFromPage(): PostShowStatePostData | null {
        const postContainer = postShowForm.getPostDetail(document);
        if (!(postContainer instanceof HTMLElement)) {
            return null;
        }

        const postId = toPositiveIntOrNull(postContainer.dataset.postId);
        if (postId === null) {
            return null;
        }

        const titleElement = postShowForm.getPostDetailTitle(postContainer);
        const contentElement = postShowForm.getPostDetailContent(postContainer);
        const authorElement = postShowForm.getPostAuthorName(postContainer);
        const dateElement = postShowForm.getPostCreatedAt(postContainer);

        return {
            post_id: postId,
            post_title: titleElement?.textContent?.trim() || "",
            content: contentElement instanceof HTMLElement ? contentElement.innerHTML : "",
            author: authorElement?.textContent?.trim() || "",
            created_at: dateElement?.textContent?.trim() || "",
            board_id: this.boardId || undefined,
        };
    }

    public getUserIdFromPage(): number | null {
        const postDetail = postShowForm.getPostDetail(document);
        const dropdownContainer =
            postDetail instanceof HTMLElement ? postShowForm.getPostDropdownContainer(postDetail) : null;
        const dropdownUserId = dropdownContainer?.dataset.userId?.trim();
        if (dropdownUserId) {
            const normalizedDropdownUserId = Number(dropdownUserId);
            return Number.isInteger(normalizedDropdownUserId) && normalizedDropdownUserId > 0
                ? normalizedDropdownUserId
                : null;
        }

        const metaTag = postShowForm.getUserIdMeta(document);
        if (metaTag instanceof HTMLMetaElement && metaTag.content) {
            const normalizedMetaUserId = Number(metaTag.content);
            return Number.isInteger(normalizedMetaUserId) && normalizedMetaUserId > 0 ? normalizedMetaUserId : null;
        }

        const authUser = phpAuthUser();
        if (typeof authUser?.user_id === "number" && Number.isInteger(authUser.user_id) && authUser.user_id > 0) {
            return authUser.user_id;
        }

        const bodyUserId = document.body.dataset.userId;
        if (bodyUserId) {
            const normalizedBodyUserId = Number(bodyUserId);
            return Number.isInteger(normalizedBodyUserId) && normalizedBodyUserId > 0 ? normalizedBodyUserId : null;
        }

        return null;
    }

    public getCurrentPost(): PostShowStatePostData | null {
        return this.currentPost;
    }

    public setCurrentPost(postData: PostShowStatePostData | null): void {
        this.currentPost = postData;
    }

    public updatePostData(postId: number, updatedData: Partial<PostShowStatePostData>): void {
        const currentPostId = Number(this.currentPost?.post_id);
        if (this.currentPost && Number.isInteger(currentPostId) && currentPostId === postId) {
            this.currentPost = { ...this.currentPost, ...updatedData };
        }
    }

    public getCurrentEditingPost(): EditingPostState | null {
        return this.editingPost;
    }

    public setCurrentEditingPost(postId: number, postData: PostShowStatePostData | null = null): void {
        const currentPostId = Number(this.currentPost?.post_id);
        this.editingPost = {
            id: postId,
            originalData:
                postData ||
                (this.currentPost && Number.isInteger(currentPostId) && currentPostId === postId
                    ? this.currentPost
                    : null),
            isEditing: true,
        };
    }

    public clearCurrentEditingPost(): void {
        this.editingPost = null;
    }

    public isEditing(postId: number | null = null): boolean {
        if (!this.editingPost) {
            return false;
        }

        if (postId !== null) {
            return this.editingPost.id === postId;
        }

        return this.editingPost.isEditing;
    }

    public getBoardId(): string | null {
        return this.boardId;
    }

    public addError(error: string): void {
        this.errors.push({
            message: error,
            timestamp: new Date().toISOString(),
        });
    }
}

export { PostShowStateHandler };
